import type { Mix, Session, Stream } from "../types/model.types";
import { isAndroid } from "./device-detection";

export interface MediaSessionCallbacks {
  onPlay?: () => void;
  onPause?: () => void;
  onStop?: () => void;
}

export interface MediaSessionHandlerOptions {
  audioElement?: HTMLAudioElement | null;
}

export interface MediaSessionMetadata {
  title?: string;
  artist?: string;
  album?: string;
  artwork?: Array<{
    src: string;
    sizes?: string;
    type?: string;
  }>;
}

/**
 * MediaSession utility class for managing lock screen controls and metadata
 */
export class MediaSessionManager {
  private callbacks: MediaSessionCallbacks = {};
  private metadata: MediaSessionMetadata | null = null;

  /**
   * Checks if MediaSession API is available
   */
  public static isSupported(): boolean {
    return typeof navigator !== "undefined" && "mediaSession" in navigator;
  }

  /**
   * Gets the MediaSession API instance
   */
  private getMediaSession(): MediaSession | null {
    if (!MediaSessionManager.isSupported()) {
      console.warn("MediaSession is not supported");
      return null;
    }
    return navigator.mediaSession;
  }

  /**
   * Creates MediaSession metadata from Mix, Session, and Stream
   */
  public static createMetadata(
    mix?: Mix | null,
    session?: Session | null,
    stream?: Stream | null,
    language?: string,
  ): MediaSessionMetadata {
    // Use session image if available and non-empty, otherwise fall back to mix image
    const imageUrl =
      (session?.image && session.image.trim()) ||
      (mix?.image && mix.image.trim()) ||
      "";

    return {
      title: session?.name ?? mix?.name ?? "",
      artist: language ?? session?.subtitle ?? "",
      album: stream?.description ?? "",
      artwork: imageUrl
        ? [
            {
              src: imageUrl,
              type: "image/jpg",
            },
          ]
        : [],
    };
  }

  /**
   * Sets up MediaSession metadata
   */
  public setMetadata(metadata: MediaSessionMetadata): void {
    const mediaSession = this.getMediaSession();
    if (!mediaSession) {
      return;
    }

    this.metadata = metadata;

    mediaSession.metadata = new MediaMetadata({
      title: metadata.title ?? "",
      artist: metadata.artist ?? "",
      album: metadata.album ?? "",
      artwork: metadata.artwork ?? [],
    });
  }

  /**
   * Sets up MediaSession action handlers
   */
  public setHandlers(
    callbacks: MediaSessionCallbacks,
    options?: MediaSessionHandlerOptions,
  ): void {
    const mediaSession = this.getMediaSession();
    if (!mediaSession) {
      return;
    }

    this.callbacks = callbacks;
    const { audioElement } = options || {};

    // Disable unwanted actions
    mediaSession.setActionHandler("seekforward", null);
    mediaSession.setActionHandler("seekbackward", null);
    mediaSession.setActionHandler("previoustrack", null);
    mediaSession.setActionHandler("nexttrack", null);

    // Set up play handler
    // Volume control is handled by AudioGraph.setVolume() via the callbacks
    // (player.playStream → audioGraph.setVolume). Do not set gain directly here.
    if (callbacks.onPlay) {
      mediaSession.setActionHandler("play", () => {
        // Android-specific: Set playback state and play audio element
        if (isAndroid() && mediaSession) {
          mediaSession.playbackState = "playing";
          if (audioElement) {
            audioElement.play().catch((error) => {
              console.error("Failed to play audio element:", error);
            });
          }
        }

        callbacks.onPlay!();
      });
    } else {
      mediaSession.setActionHandler("play", null);
    }

    // Set up pause handler
    // Volume control is handled by AudioGraph.stop() via the callbacks
    // (player.stop → audioGraph.stop). Do not set gain directly here.
    if (callbacks.onPause) {
      mediaSession.setActionHandler("pause", () => {
        // Android-specific: Set playback state and pause audio element
        if (isAndroid() && mediaSession) {
          mediaSession.playbackState = "paused";
          if (audioElement) {
            audioElement.pause();
          }
        }

        callbacks.onPause!();
      });
    } else {
      mediaSession.setActionHandler("pause", null);
    }

    // Set up stop handler
    // Volume control is handled by AudioGraph.stop() via the callbacks
    // (player.stop → audioGraph.stop). Do not set gain directly here.
    if (callbacks.onStop) {
      mediaSession.setActionHandler("stop", () => {
        // Android-specific: Set playback state and pause audio element
        if (isAndroid() && mediaSession) {
          mediaSession.playbackState = "paused";
          if (audioElement) {
            audioElement.pause();
          }
        }

        callbacks.onStop!();
      });
    } else {
      mediaSession.setActionHandler("stop", null);
    }
  }

  /**
   * Updates MediaSession playback state
   */
  public setPlaybackState(state: "none" | "paused" | "playing"): void {
    const mediaSession = this.getMediaSession();
    if (!mediaSession) {
      return;
    }

    mediaSession.playbackState = state;
  }

  /**
   * Sets up MediaSession for a stream with metadata and callbacks
   * This is a convenience method that combines metadata setup and handler setup
   */
  public setupForStream(
    mix: Mix | null | undefined,
    session: Session | null | undefined,
    stream: Stream | null | undefined,
    callbacks: MediaSessionCallbacks,
    language?: string,
  ): void {
    if (!MediaSessionManager.isSupported()) {
      return;
    }

    const metadata = MediaSessionManager.createMetadata(
      mix,
      session,
      stream,
      language,
    );
    this.setMetadata(metadata);
    this.setHandlers(callbacks);
  }

  /**
   * Gets the current callbacks
   */
  public getCallbacks(): MediaSessionCallbacks {
    return { ...this.callbacks };
  }

  /**
   * Gets the current metadata
   */
  public getMetadata(): MediaSessionMetadata | null {
    return this.metadata ? { ...this.metadata } : null;
  }

  /**
   * Clears all handlers and metadata, and sets playback state to none
   */
  public clear(): void {
    const mediaSession = this.getMediaSession();
    if (!mediaSession) {
      return;
    }

    // Set playback state to none
    this.setPlaybackState("none");

    // Clear all handlers
    mediaSession.setActionHandler("play", null);
    mediaSession.setActionHandler("pause", null);
    mediaSession.setActionHandler("stop", null);
    mediaSession.setActionHandler("seekforward", null);
    mediaSession.setActionHandler("seekbackward", null);
    mediaSession.setActionHandler("previoustrack", null);
    mediaSession.setActionHandler("nexttrack", null);

    // Clear metadata
    mediaSession.metadata = null;
    this.callbacks = {};
    this.metadata = null;
  }
}
